import React, { useState } from 'react';
import styled from '@emotion/styled';
import {
  FormRow,
  FormLabel,
  FormFieldWrapper,
  FormRowProps,
  FormNumberInput
} from '../form-input';
import { Row, Container } from '../layout';
import { Spacer } from '../spacer';

const LayoutEditor = styled(Container)``;

const LayoutEditorRow = styled(Row)`
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
`;

const LayoutSide = styled.div`
  flex: 0 0 60px;

  &:first-child {
  }
`;

const LayoutMiddle = styled.div`
  flex: 0 0 60px;
`;

const LayoutSpacingVertical = styled.div<{ active?: boolean }>`
  background: ${p => (p.active ? '#ffb123' : '#ffdfa5')};
  flex: 0 0 112px;
  height: 8px;
`;

const LayoutSpacingHorizontal = styled.div<{ active?: boolean }>`
  background: ${p => (p.active ? '#ffb123' : '#ffdfa5')};
  flex: 0 0 8px;
  height: 40px;
  margin: 4px 0;
`;

const LayoutButtonWrapper = styled.div`
  flex: 0 0 136px;
`;

const LayoutButtonPlaceholder = styled(Spacer)`
  margin: 4px;
  background: ${p => p.theme.palette.primary.main};
`;

const LayoutEditorNumberInput = styled(FormNumberInput)`
  input {
    padding: 8px 4px 8px 14px;
  }
`;

type Spacing = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

type Focused = 'top' | 'left' | 'right' | 'bottom';

function reconsituteSpacing({ top, left, bottom, right }: Spacing): string {
  return `${top}px ${right}px ${bottom}px ${left}px`;
}

function parseSpacing(value: string): Spacing {
  const sections = value
    .split(' ')
    .map(x => parseFloat(x))
    .map(x => (isNaN(x) ? 0 : x));

  // '4px' -> { top: 4, right: 4, bottom: 4, left: 4 }
  if (sections.length === 1) {
    const [singleValue] = sections;

    return {
      top: singleValue,
      right: singleValue,
      bottom: singleValue,
      left: singleValue
    };
  }
  // '4px 8px' -> { top: 4, right: 8, bottom: 4, left: 8 }
  else if (sections.length === 2) {
    const [vertical, horizontal] = sections;

    return {
      top: vertical,
      right: horizontal,
      bottom: vertical,
      left: horizontal
    };
  }
  // '4px 8px 12px 16px' -> { top: 4, right: 8, bottom: 12, left: 16 }
  else {
    const [top, right, bottom, left] = sections;

    return { top, right, bottom, left };
  }
}

export interface SpacingFieldProps extends FormRowProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
}

export function SpacingField(props: SpacingFieldProps) {
  const { label, value, onChange, ...formRowProps } = props;

  const [spacing, setSpacing] = useState<Spacing>(parseSpacing(value));
  const [focused, setFocused] = useState<Focused>();

  const handleSpacingChange = (side: keyof Spacing, val: number) => {
    const newSpacing = { ...spacing, [side]: val };
    setSpacing(newSpacing);
    onChange(reconsituteSpacing(newSpacing));
  };

  return (
    <FormRow {...formRowProps}>
      <FormLabel>{label}</FormLabel>
      <FormFieldWrapper>
        <LayoutEditor>
          <LayoutEditorRow>
            <LayoutSide />
            <LayoutMiddle>
              <LayoutEditorNumberInput
                value={spacing.top}
                onChange={(value: number) => handleSpacingChange('top', value)}
                onFocus={() => setFocused('top')}
              />
            </LayoutMiddle>
            <LayoutSide />
          </LayoutEditorRow>
          <LayoutEditorRow>
            <LayoutSide>
              <LayoutEditorNumberInput
                value={spacing.left}
                onChange={(value: number) => handleSpacingChange('left', value)}
                onFocus={() => setFocused('left')}
              />
            </LayoutSide>
            <LayoutButtonWrapper>
              <Row>
                <Spacer />
                <LayoutSpacingVertical active={focused === 'top'} />
                <Spacer />
              </Row>
              <Row>
                <LayoutSpacingHorizontal active={focused === 'left'} />
                <LayoutButtonPlaceholder />
                <LayoutSpacingHorizontal active={focused === 'right'} />
              </Row>
              <Row>
                <Spacer />
                <LayoutSpacingVertical active={focused === 'bottom'} />
                <Spacer />
              </Row>
            </LayoutButtonWrapper>
            <LayoutSide>
              <LayoutEditorNumberInput
                value={spacing.right}
                onChange={(value: number) =>
                  handleSpacingChange('right', value)
                }
                onFocus={() => setFocused('right')}
              />
            </LayoutSide>
          </LayoutEditorRow>
          <LayoutEditorRow>
            <LayoutSide />
            <LayoutMiddle>
              <LayoutEditorNumberInput
                value={spacing.bottom}
                onChange={(value: number) =>
                  handleSpacingChange('bottom', value)
                }
                onFocus={() => setFocused('bottom')}
              />
            </LayoutMiddle>
            <LayoutSide />
          </LayoutEditorRow>
        </LayoutEditor>
      </FormFieldWrapper>
    </FormRow>
  );
}
